import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Manages server-side paginated, sortable, and filterable data fetching
 * for admin resource index pages.
 *
 * @param {string} endpoint  Base Rails path, e.g. '/admin_kit/dealers'
 * @param {number} perPage   Page size (default 30)
 */
const useAdminResource = (endpoint, perPage = 30) => {
  const [records, setRecords] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFiltersState] = useState({});
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: perPage,
    total_pages: 0,
    total_items: 0,
  });

  // Keep a ref to the latest fetch so stale responses are ignored
  const fetchId = useRef(0);

  const fetch = useCallback(async () => {
    fetchId.current += 1;
    const id = fetchId.current;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page, per_page: perPage });
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v != null) params.set(k, v);
    });
    if (sortBy) {
      params.set('q[s]', `${sortBy} ${sortOrder}`);
    }

    try {
      const res = await window.fetch(`${endpoint}.json?${params}`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      if (fetchId.current !== id) return; // stale
      setRecords(data.records);
      setPagination(data.pagination);
    } catch (err) {
      if (fetchId.current === id) setError(err.message);
    } finally {
      if (fetchId.current === id) setLoading(false);
    }
  }, [endpoint, page, perPage, filters, sortBy, sortOrder]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Reset to page 1 whenever filters change
  const setFilters = useCallback((newFilters) => {
    setPage(1);
    setFiltersState(newFilters);
  }, []);

  const handleSort = (field) => () => {
    if (sortBy === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  return {
    records,
    isLoading,
    error,
    pagination,
    page,
    setPage,
    filters,
    setFilters,
    sortBy,
    sortOrder,
    handleSort,
    refetch: fetch,
  };
};

export default useAdminResource;
